import React, { useState, useEffect, useCallback } from 'react';
import { BarChartOutlined } from '@ant-design/icons';
import { Drawer, Empty, Pagination, Spin, Table, Tag } from 'antd';
import TieringService, { TieringPartner, TieringSummary } from '../../services/TieringService';
import D3TierDiagram from './D3TierDiagram';
import { DRAWER_STYLES } from '../../../../styles/modalStyles';
import styles from './TierDiagram.module.css';

interface TierDiagramProps {
  rootEntityId: number;
}

const TierDiagram: React.FC<TierDiagramProps> = ({ rootEntityId }) => {
  const [summary, setSummary] = useState<TieringSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCategory, setDrawerCategory] = useState('');
  const [drawerPartners, setDrawerPartners] = useState<TieringPartner[]>([]);
  const [drawerTotal, setDrawerTotal] = useState(0);
  const [drawerPage, setDrawerPage] = useState(1);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const handleExpandCluster = useCallback(
    async (category: string) => {
      setDrawerCategory(category);
      setDrawerOpen(true);
      setDrawerLoading(true);
      setDrawerPage(1);
      try {
        const result = await TieringService.getPartnersByCategory(rootEntityId, category, 1, 50);
        setDrawerPartners(result.partners);
        setDrawerTotal(result.totalCount);
      } catch {
        // API not yet deployed — drawer will show empty state
      }
      setDrawerLoading(false);
    },
    [rootEntityId],
  );

  useEffect(() => {
    if (!rootEntityId) return;
    setLoading(true);
    setSummary(null);

    TieringService.getSummary(rootEntityId)
      .then((s) => {
        setSummary(s);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [rootEntityId]);

  const tierCount = summary?.maxDepth ?? 2;
  const renderMode = summary?.renderMode ?? 'full';

  if (loading) {
    return (
      <div className={styles.center}>
        <Spin />
      </div>
    );
  }

  if (renderMode === 'canvas') {
    return (
      <div
        style={{
          padding: 32,
          textAlign: 'center',
          background: 'rgba(26, 39, 68, 0.4)',
          borderRadius: 10,
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <BarChartOutlined style={{ fontSize: 32, color: '#ADADAD', marginBottom: 12 }} />
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8, color: '#ffffff' }}>
          Large fund structure — {summary?.directPartnerCount} partners
        </div>
        <div style={{ fontSize: 13, color: '#ADADAD', marginBottom: 16 }}>
          High-performance canvas rendering for funds with 500+ partners. Coming in the next
          release.
        </div>
        <div style={{ fontSize: 12, color: '#ADADAD' }}>
          Use the List tab to view all relationships.
        </div>
      </div>
    );
  }

  if (!rootEntityId) {
    return <Empty description="No tiering relationships found" style={{ padding: 64 }} />;
  }

  return (
    <div style={{ height: Math.max(500, (tierCount + 1) * 150 + 100), position: 'relative' }}>
      {summary && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 10,
            fontSize: 11,
            color: 'rgba(255,255,255,0.50)',
            background: 'rgba(10,10,20,0.70)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 6,
            padding: '3px 8px',
            backdropFilter: 'blur(8px)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {summary.directPartnerCount} partners · {renderMode} mode
        </div>
      )}

      <D3TierDiagram
        rootEntityId={rootEntityId}
        renderMode={renderMode}
        onClusterClick={handleExpandCluster}
      />

      <Drawer
        title={`${drawerCategory} — ${drawerTotal} partners`}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        styles={{ ...DRAWER_STYLES, body: { ...DRAWER_STYLES.body, padding: 0 } }}
      >
        {drawerLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <>
            <Table<TieringPartner>
              dataSource={drawerPartners}
              rowKey="partnerId"
              size="small"
              pagination={false}
              scroll={{ y: 'calc(100vh - 200px)' }}
              columns={[
                {
                  title: 'Partner',
                  dataIndex: 'partnerName',
                  ellipsis: true,
                  render: (name: string, r: TieringPartner) => (
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 12 }}>{name}</div>
                      <div style={{ fontSize: 11, color: '#ADADAD' }}>
                        {r.city}
                        {r.state ? `, ${r.state}` : ''}
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'EIN',
                  dataIndex: 'einSsn',
                  width: 100,
                  render: (v: string) => (
                    <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span>
                  ),
                },
                {
                  title: 'Class',
                  dataIndex: 'interestClass',
                  width: 55,
                  render: (v: string) =>
                    v ? <Tag style={{ fontSize: 10 }}>{v}</Tag> : null,
                },
                {
                  title: 'Ownership',
                  dataIndex: 'ownershipPct',
                  width: 80,
                  align: 'right' as const,
                  render: (v: number) => (
                    <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {Number(v).toFixed(3)}%
                    </span>
                  ),
                },
              ]}
            />
            {drawerTotal > 50 && (
              <div
                style={{
                  padding: '12px 16px',
                  borderTop: '0.5px solid rgba(255,255,255,0.1)',
                }}
              >
                <Pagination
                  current={drawerPage}
                  total={drawerTotal}
                  pageSize={50}
                  size="small"
                  showTotal={(t) => `${t} partners total`}
                  onChange={async (page) => {
                    setDrawerPage(page);
                    setDrawerLoading(true);
                    try {
                      const r = await TieringService.getPartnersByCategory(
                        rootEntityId,
                        drawerCategory,
                        page,
                        50,
                      );
                      setDrawerPartners(r.partners);
                    } catch {
                      // ignore
                    }
                    setDrawerLoading(false);
                  }}
                />
              </div>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default TierDiagram;
